export type GameCode = 'lastdigit' | 'sicbo' | 'roulette';

export type RoomHost = {
  id?: string;
  name?: string;
  username?: string;
  initial?: string;
  flag?: string;
  vipLevel?: number;
  level?: number;
};

export type Room = {
  id: string;
  code: string;
  game: GameCode;
  label: string;
  tier: string;
  enabled: boolean;
  /** Server-side ESTIMATE of occupancy. Not what a player should be shown —
   *  see `roomOnlineCount`. */
  online: number;
  /** Real presence, same source as /api/rooms/presence. Absent when the
   *  presence service did not report. */
  presenceOnline?: number;
  onlineSource?: 'presence' | 'estimate' | string;
  capacity: number;
  minVipLevel: number;
  chipValuesBrl: number[];
  chipValuesUsdt?: number[];
  perRoundMaxBrl: number;
  perRoundMaxUsdt?: number;
  poolCapBrl: number;
  poolCapUsdt?: number;
  rakebackPct?: number;
  rakebackBrlMinStake?: number;
  rakebackUsdtMinStake?: number;
  thisRoundBets: number;
  gameBetsConfig?: unknown;
  hostUserId?: string | null;
  hostName?: string;
  hostUser?: RoomHost | null;
  host_user?: RoomHost | null;
  preview?: {
    user?: string;
    text?: string;
  };
};

export type RoomsResponse = {
  rooms: Room[];
  config_version?: number;
  generated_at?: number;
};

export type GamePresenceResponse = {
  generated_at: number;
  refresh_after_sec: number;
  presence_ttl_sec: number;
  /**
   * Distinct visitors online across the whole site right now — hall heartbeats
   * (`POST /api/v1/hall/presence`, sent by every open client) unioned with the
   * room heartbeats above. This is the number behind the lobby's "N playing
   * right now". Absent from a backend that predates it.
   */
  total?: number;
  games: Record<GameCode, number>;
};

/**
 * How many people a player is told are in a room, right now.
 *
 * /api/rooms carries two different numbers: `presenceOnline` (real, the same
 * source /api/rooms/presence aggregates) and `online` (a server-side estimate).
 * The lobby's game cards read the presence endpoint while the room header read
 * `online`, so the same room announced "0 online agora" in the lobby and
 * "8/100 online" one tap later. Presence wins wherever it is reported — a real
 * zero is information, not a gap — and the estimate is only a fallback for when
 * the field is missing entirely.
 */
export function roomOnlineCount(room: Pick<Room, 'online' | 'presenceOnline'>): number {
  return typeof room.presenceOnline === 'number' ? room.presenceOnline : Number(room.online || 0);
}
